import React, { useRef } from 'react';

const parseDateParts = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return { year: '', month: '', day: '' };
  const parts = dateStr.split('-');
  return { year: parts[0] || '', month: parts[1] || '', day: parts[2] || '' };
};

const buildDateStr = (year, month, day) => {
  const ny = String(year || '').replace(/\D/g, '').slice(0, 4);
  const nm = String(month || '').replace(/\D/g, '').slice(0, 2);
  const nd = String(day || '').replace(/\D/g, '').slice(0, 2);
  const nmStr = nm.length === 2 ? String(nm).padStart(2, '0') : nm;
  const ndStr = nd.length === 2 ? String(nd).padStart(2, '0') : nd;
  if ([ny, nm, nd].every(Boolean))
    return `${ny}-${nmStr}-${ndStr}`;
  if (ny && nm) return `${ny}-${nmStr}${nd ? '-' + ndStr : ''}`;
  if (ny) return ny;
  return '';
};

/** 연·월·일 분리 입력 + 캘린더 선택 병행, 연도 4자리/월 2자리 입력 시 자동으로 다음 필드로 포커스 이동 */
const DateInput = ({ value = '', onChange, style = {}, inputStyle = {}, required, disabled, showCalendar = true }) => {
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const parts = parseDateParts(value);

  const handlePartChange = (part, rawValue) => {
    const numeric = rawValue.replace(/\D/g, '');
    let ny = parts.year, nm = parts.month, nd = parts.day;
    if (part === 'year') ny = numeric.slice(0, 4);
    else if (part === 'month') nm = numeric.slice(0, 2);
    else if (part === 'day') nd = numeric.slice(0, 2);
    onChange(buildDateStr(ny, nm, nd));
    if (part === 'year' && numeric.length >= 4) setTimeout(() => monthRef.current?.focus(), 0);
    else if (part === 'month' && numeric.length >= 2) setTimeout(() => dayRef.current?.focus(), 0);
  };

  const handleKeyUp = (part, rawValue) => {
    const numeric = rawValue.replace(/\D/g, '');
    if (part === 'year' && numeric.length >= 4) monthRef.current?.focus();
    else if (part === 'month' && numeric.length >= 2) dayRef.current?.focus();
  };

  const calendarValue = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';

  const baseStyle = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    ...inputStyle
  };
  const yearStyle = { ...baseStyle, width: '80px', textAlign: 'center' };
  const monthStyle = { ...baseStyle, width: '50px', textAlign: 'center' };
  const dayStyle = { ...baseStyle, width: '50px', textAlign: 'center' };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', ...style }}>
      <input
        type="text"
        inputMode="numeric"
        maxLength={4}
        placeholder="연도"
        value={parts.year}
        onChange={(e) => handlePartChange('year', e.target.value)}
        onKeyUp={(e) => handleKeyUp('year', e.target.value)}
        style={yearStyle}
        required={required}
        disabled={disabled}
      />
      <span>년</span>
      <input
        ref={monthRef}
        type="text"
        inputMode="numeric"
        maxLength={2}
        placeholder="월"
        value={parts.month}
        onChange={(e) => handlePartChange('month', e.target.value)}
        onKeyUp={(e) => handleKeyUp('month', e.target.value)}
        style={monthStyle}
        disabled={disabled}
      />
      <span>월</span>
      <input
        ref={dayRef}
        type="text"
        inputMode="numeric"
        maxLength={2}
        placeholder="일"
        value={parts.day}
        onChange={(e) => handlePartChange('day', e.target.value)}
        style={dayStyle}
        disabled={disabled}
      />
      <span>일</span>
      {showCalendar && (
        <>
          <span style={{ marginLeft: '4px', color: '#999' }}>|</span>
          <input
            type="date"
            value={calendarValue}
            onChange={(e) => onChange(e.target.value || '')}
            style={{ ...baseStyle, marginLeft: '8px', minWidth: '140px' }}
            disabled={disabled}
            title="캘린더에서 선택"
          />
        </>
      )}
    </div>
  );
};

export default DateInput;
export { parseDateParts, buildDateStr };
